/**
 * PincodeDialog — first-visit soft prompt to capture the customer's pincode
 * so we can gate delivery. Also opened from the header "Deliver to" chip.
 * Purely UX: no backend calls, everything driven by StoreContext.
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, BellRing, CheckCircle2, XCircle } from "lucide-react";
import { useStore } from "@/contexts/StoreContext";
import { toast } from "sonner";

export default function PincodeDialog({ open, onOpenChange }) {
  const { pincode, setPincode, zones, serviceablePincodes } = useStore();
  const [entered, setEntered] = useState(pincode || "");
  const [checked, setChecked] = useState(false);

  const known = new Set([
    ...(serviceablePincodes || []).map(String),
    ...(zones || []).flatMap((z) => (z.pincodes || []).map(String)),
  ]);
  const serviceable = entered.length === 6 && (known.size === 0 || known.has(entered));

  const check = () => {
    if (!/^\d{6}$/.test(entered)) { toast.error("Enter a valid 6-digit pincode"); return; }
    setChecked(true);
    setPincode(entered);
    if (serviceable) { toast.success(`Great! We deliver to ${entered}.`); onOpenChange(false); }
  };

  const notify = () => { toast.success("We'll notify you when we deliver to your area."); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pincode-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" /> Enter your pincode
          </DialogTitle>
          <DialogDescription>
            We&apos;ll check if Babu Baazar delivers to your area.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            value={entered}
            onChange={(e) => { setEntered(e.target.value.replace(/\D/g, "").slice(0, 6)); setChecked(false); }}
            placeholder="e.g. 560001"
            inputMode="numeric"
            maxLength={6}
            className="text-lg font-mono tracking-widest text-center h-12"
            onKeyDown={(e) => { if (e.key === "Enter") check(); }}
            data-testid="pincode-input"
          />

          {checked && entered.length === 6 && (
            serviceable ? (
              <div className="rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200 p-3 flex items-start gap-2 text-sm" data-testid="pincode-yes">
                <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
                <div>We deliver to <span className="font-semibold">{entered}</span>. You&apos;re all set!</div>
              </div>
            ) : (
              <div className="rounded-lg bg-destructive/5 text-destructive border border-destructive/20 p-3 space-y-2" data-testid="pincode-no">
                <div className="flex items-start gap-2 text-sm">
                  <XCircle className="w-5 h-5 mt-0.5 shrink-0" />
                  <div>Sorry, we don&apos;t deliver to <span className="font-semibold">{entered}</span> yet. You can still browse — we&apos;ll notify you when we do.</div>
                </div>
                <Button variant="outline" size="sm" onClick={notify} className="w-full" data-testid="notify-me-btn">
                  <BellRing className="w-4 h-4 mr-1.5" /> Notify me when available
                </Button>
              </div>
            )
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="pincode-later">Skip for now</Button>
          <Button onClick={check} className="btn-primary-glow" data-testid="pincode-check">Check availability</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
